// Adapted from beautifului.dev (MIT, © 2026 Shane Levine).
/**
 * The streaming *feel* of an assistant answer (ORA-598): the caret at the
 * writing edge, the one-shot reveal a markdown block gets when it settles, and
 * the whole-answer fade for turns whose text arrives in a single piece.
 *
 * All three are pure helpers plus one presentational span, so the renderer in
 * `ChatMarkdown` stays a renderer: none of this reparses markdown, and none of
 * it holds state of its own.
 */
import { Fragment, cloneElement, isValidElement, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export const CHAT_STREAM_CARET_TEST_ID = 'chat-stream-caret';

/**
 * The bar at the writing edge.
 *
 * `bg-fg-primary` rather than the brief's first suggestion of `bg-accent`:
 * `--color-accent` is a muted *surface* in Ora's scale, so a caret painted
 * with it is invisible on the surface it sits on. The body colour reads in
 * both themes and is what a text cursor is anyway.
 *
 * Decorative, so `aria-hidden`: the answer already announces itself through
 * the live region in `ChatMessageItem`.
 */
export const StreamCaret = () => (
  <span
    aria-hidden="true"
    data-testid={CHAT_STREAM_CARET_TEST_ID}
    className="ml-px inline-block h-[1em] w-[2px] translate-y-[0.15em] rounded-full bg-fg-primary animate-chat-caret-blink"
  />
);

/**
 * Blocks whose flow the caret can join. A paragraph or a heading ends in an
 * inline run, so the caret belongs after its last inline child; a table, a
 * list or anything else would push the caret onto a line of its own or, worse,
 * into a table's markup, so for those it goes after the block instead.
 */
const CARET_INLINE_BLOCKS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

/**
 * Puts the caret at the end of the last block that is still being written.
 *
 * Returns a new node list; the blocks themselves are untouched, so the parse
 * cache upstream keeps handing out the same elements.
 */
export const appendStreamCaret = (
  blocks: ReactNode,
  caret: ReactNode
): ReactNode => {
  const nodes = Array.isArray(blocks) ? blocks : [blocks];
  const lastIndex = nodes.length - 1;
  const last: ReactNode = nodes[lastIndex];

  if (
    isValidElement<{ children?: ReactNode }>(last) &&
    typeof last.type === 'string' &&
    CARET_INLINE_BLOCKS.has(last.type)
  ) {
    const withCaret = cloneElement(
      last,
      undefined,
      <>
        {last.props.children}
        {caret}
      </>
    );

    return [...nodes.slice(0, lastIndex), withCaret];
  }

  // A stable key, so a growing table keeps its identity as rows land instead
  // of being remounted by a caret shuffling about behind it.
  return [
    ...nodes,
    <Fragment key={CHAT_STREAM_CARET_TEST_ID}>{caret}</Fragment>,
  ];
};

export const CHAT_REVEAL_CLASS = 'animate-chat-reveal';

/**
 * The identity of a markdown block, for the purpose of "has the reader seen
 * this yet?".
 *
 * A block is a different string depending on where the stream has got to: the
 * block being written is `Second one.` before its line ends and
 * `Second one.\n` after, and once it settles the split hands it back with no
 * newline at all. Trimming makes those the same block.
 */
export const blockRevealKey = (text: string): string => text.trim();

const revealNode = (node: ReactNode): ReactNode => {
  if (!isValidElement<{ className?: string }>(node)) return node;

  return cloneElement(node, {
    className: cn(node.props.className, CHAT_REVEAL_CLASS),
  });
};

/**
 * Marks a block for its one-shot entrance.
 *
 * A block settles by moving from the "being written" fragment into the
 * "settled" one, which remounts it — so the class alone is the whole
 * animation. Blocks already settled when the message first rendered never get
 * it, which is what keeps a history load still. Callers cache the result so a
 * re-render hands React the identical elements and CSS has no reason to
 * restart.
 */
export const withRevealClass = (parsed: ReactNode): ReactNode =>
  Array.isArray(parsed) ? parsed.map(revealNode) : revealNode(parsed);

/**
 * Text this long arriving in one go is not a stream — it is an answer the
 * backend buffered whole (help-doc, image and locator turns all do), and
 * dropping it in as a wall of text under a thinking indicator reads as a
 * glitch. Below it, normal deltas must not fade or every answer flickers.
 */
export const WHOLE_ANSWER_FADE_MIN_CHARS = 400;

/**
 * Should the answer cross-fade in as one piece?
 *
 * True only for the very first text of a live turn (`prevLength === 0`) when
 * that first piece is already a whole answer. Anything that grows from a short
 * first delta is a real stream and is left alone.
 */
export const shouldFadeInWholeAnswer = (
  prevLength: number,
  nextLength: number
): boolean => prevLength === 0 && nextLength > WHOLE_ANSWER_FADE_MIN_CHARS;
